import {
  DisplayStatus,
  QuestionAnswer as PrismaQuestionAnswer,
  QuestionAnswerContentRevision,
  QuestionAnswerUserReactionMap,
  RevisionStatus,
  DictionaryItem_QuestionAnswer,
} from "@prisma/client";
import { Translate } from "next-translate";
import * as zod from "zod";
import { errorWithoutThrowInProduction } from "~/lib/logging/logHelper";
import { dateAgoFromToday } from "~/lib/utils/dateHelper";
import { DictionaryItemWithParent } from "~/models/dictionaryItem";
import { isAdmin, UserStatus } from "~/models/userStatus";
import { LocaleEnum } from "~/services/locale/locale";
import { ProjectPrivate } from "../project/private";

/* --- CONSTANT --- */
// default locale when create/update questionAnswer
export const DEFAULT_QA_LOCALE = LocaleEnum.ja;
export const QUESTION_ANSWER_KEY_VISUAL_CREATE_SCHEMA = zod.object({
  catchPhrase: zod
    .string()
    .nonempty({ message: "キャッチフレーズを入力してください" })
    .min(5, {
      message: `5文字以上、20文字以下にしてください`,
    })
    .max(20, {
      message: `5文字以上、20文字以下にしてください`,
    }),
  imageUrl: zod.string().nonempty({ message: "画像を選択してください" }),
  supplementaryText: zod
    .string()
    .max(40, {
      message: `40文字以下にしてください`,
    })
    .nullable(),
});

export const QUESTION_ANSWER_SCHEMA = (t: Translate) =>
  zod.object({
    answerContent: zod.string(),
    answerSummaryContent: zod
      .string()
      .nonempty({
        message: t("form:validation.empty-input", {
          name: t("question:label.answer-summary-content"),
        }),
      })
      .min(60, {
        message: t("form:validation.min-max", { max: 200, min: 60 }),
      })
      .max(200, {
        message: t("form:validation.min-max", { max: 200, min: 60 }),
      })
      .refine((data: string) => !/[\n\r]/g.test(data), {
        message: t("form:validation.no-line-break", {
          name: t("question:label.answer-summary-content"),
        }),
      }),
    locale: zod.string().nonempty({
      message: t("form:validation.empty-input", {
        name: t("question:label.locale"),
      }),
    }),
    privateQaTargetWordId: zod.string().nonempty(),
    questionContent: zod
      .string()
      .nonempty({
        message: t("form:validation.empty-input", {
          name: t("question:label.question-content"),
        }),
      })
      .min(10, {
        message: t("form:validation.min-max", { max: 80, min: 10 }),
      })
      .max(80, {
        message: t("form:validation.min-max", { max: 80, min: 10 }),
      }),
    slug: zod
      .string()
      .nonempty({
        message: t("form:validation.empty-input", {
          name: t("question:label.slug"),
        }),
      })
      .refine((value) => slugValidate(value), {
        message: t("form:validation.invalid-format"),
      }),
    topicId: zod.string(),
  });

export const QUESTION_ANSWER_FOR_ADMIN_SCHEMA = (t: Translate) =>
  zod.object({
    contributorUserIds: zod.string().nullable(),
    firstAnsweredUserId: zod.string().nonempty({
      message: t("form:validation.empty-select", {
        name: "表示ライター名",
      }),
    }),
    projectId: zod.string().nonempty({
      message: t("form:validation.empty-select", {
        name: t("common:label-project"),
      }),
    }),
  });

export const REWRITE_QA_SCHEMA = zod.object({
  requestContent: zod
    .string()
    .nonempty({ message: "依頼内容を入力してください" }),
  requestWriterUserId: zod
    .string()
    .nonempty({ message: "依頼するクリエイターを入力してください" }),
});

/* --- ENUM --- */
export enum QaSortEnum {
  createdTime = "createdUnixTime",
  pageView = "pageViewCount",
}

export enum UserReactionEnum {
  smile = "smile",
  thumbsUp = "thumbsUp",
}

/* --- INTERFACE --- */
// question answer without private fields
export interface QuestionAnswer
  extends Omit<
    PrismaQuestionAnswer,
    | "privateLastUpdatedUserId"
    | "privateSearchAnalytics"
    | "privateQaTargetWord"
    | "privateQaTargetWordId"
  > {
  dictionaryItems?: (DictionaryItem_QuestionAnswer & {
    dictionaryItem: DictionaryItemWithParent;
  })[];
  userReactionMap?: QuestionAnswerUserReactionMap[];
}

export interface QaIDictionary {
  [index: string]: QuestionAnswer;
}

export interface QaSummary {
  conversionCountAverageLast30Days: number;
  fbHelpfulCount: number;
  fbHelpfulCountAverageLast30Days: number;
  fbHelpfulCountLast30Days: number;
  pageViewCount: number;
  pageViewCountAverageLast30Days: number;
  pageViewCountLast30Days: number;
  qaCount: number;
  textCountAverage: number;
  userReactionCount: number;
}

/* --- TYPE --- */
export type QaCustomSortKey = "helpfulAndUserReaction" | "oldestPublished";
export type QaSortKey =
  | keyof Pick<
      QuestionAnswer,
      | "createdAt"
      | "fbHelpfulCount"
      | "pageViewCount"
      | "pageViewCountLast30Days"
      | "publishedAt"
      | "updatedAt"
    >
  | QaCustomSortKey;

/* --- FUNCTION --- */
export function calculateHelpfulAndUserReactionCount(
  questionAnswer?: Pick<
    QuestionAnswer,
    "fbHelpfulCount" | "userReactionCount"
  > | null
) {
  if (!questionAnswer) {
    return 0;
  }

  const { fbHelpfulCount, userReactionCount } = questionAnswer;

  return (fbHelpfulCount ?? 0) + (userReactionCount ?? 0);
}

function helpfulAndUserReactionQaSort(
  a: QuestionAnswer,
  b: QuestionAnswer
): number {
  return (
    calculateHelpfulAndUserReactionCount(b) -
    calculateHelpfulAndUserReactionCount(a)
  );
}

export function isChangeableToDraftQa({
  displayStatus,
}: {
  displayStatus: QuestionAnswer["displayStatus"];
}): boolean {
  return (
    displayStatus === DisplayStatus.created ||
    displayStatus === DisplayStatus.questioned
  );
}

export function isDisplayStatusToUpdateQuestionContent(
  displayStatus: DisplayStatus
): boolean {
  return (
    displayStatus === DisplayStatus.created ||
    displayStatus === DisplayStatus.questioned ||
    displayStatus === DisplayStatus.draft
  );
}

export function isDeletableQuestionAnswer({
  questionAnswer,
  userId,
  role,
}: {
  questionAnswer: QuestionAnswer;
  userId: string;
  role: UserStatus["role"];
}): boolean {
  if (isAdmin(role)) {
    return (
      questionAnswer.displayStatus === DisplayStatus.created ||
      questionAnswer.displayStatus === DisplayStatus.draft ||
      questionAnswer.displayStatus === DisplayStatus.questioned
    );
  } else {
    // allow deletion when the qa is not associated with a task or talk room
    return (
      !questionAnswer.talkRoomId &&
      questionAnswer.displayStatus === DisplayStatus.created &&
      userId === questionAnswer.firstAnsweredUserId
    );
  }
}

export function isReWriteAbleQa({
  lastQaRevision,
  projectPrivate,
  qa,
}: {
  lastQaRevision?: QuestionAnswerContentRevision;
  projectPrivate?: ProjectPrivate;
  qa: QuestionAnswer;
}): boolean {
  if (!lastQaRevision || !projectPrivate?.rewriteDaysFromLastRevision || !qa) {
    return false;
  }

  const pvTarget = (projectPrivate.rewritePvPerDay || 0) * 30;
  const updatedAtISOStr =
    typeof lastQaRevision.updatedAt === "object"
      ? lastQaRevision.updatedAt.toISOString()
      : lastQaRevision.updatedAt;

  return !!(
    lastQaRevision.status === RevisionStatus.fixed &&
    qa.pageViewCountLast30Days <= pvTarget &&
    updatedAtISOStr <=
      dateAgoFromToday(projectPrivate.rewriteDaysFromLastRevision).toISOString()
  );
}

export function getAvailableQaPublishControl(
  displayStatus: QuestionAnswer["displayStatus"],
  revisionStatus?: QuestionAnswerContentRevision["status"]
) {
  if (displayStatus !== DisplayStatus.publish) {
    return "publishArticle";
  }
  if (revisionStatus === RevisionStatus.editing) {
    return "publishChanges";
  }
  return "unpublishArticle";
}

export function getDisplayStatusText({
  displayStatus,
  revisionStatus,
  t,
}: {
  displayStatus: DisplayStatus;
  revisionStatus?: QuestionAnswerContentRevision["status"];
  t: Translate;
}): string {
  switch (displayStatus) {
    case DisplayStatus.created:
      return t("question:display-status.created");
    case DisplayStatus.draft:
      return t("question:display-status.draft");
    case DisplayStatus.publish:
      if (revisionStatus === RevisionStatus.editing) {
        return t("question:display-status.publish-editing");
      }
      return t("question:display-status.publish");
    case DisplayStatus.questioned:
      return t("question:display-status.questioned");
  }
}

export function getQuestionShortContent(questionContent: string): string {
  return `${questionContent.slice(0, 20)}...`;
}

// change full-width space or two consecutive spaces in question content to a single half-width.
export function modifyQuestionContentSpace(questionContent: string): string {
  if (!questionContent) {
    return questionContent;
  }

  return (
    questionContent
      // eslint-disable-next-line no-irregular-whitespace
      .replace(/[\s　]+/, " ")
      .replace(/】\s/, "】")
      .replace(/]\s/, "]")
      .trim()
  );
}

export function qaSortFunctionForKey(key: QaCustomSortKey) {
  switch (key) {
    case "helpfulAndUserReaction":
      return helpfulAndUserReactionQaSort;
  }
}

function slugValidate(value: string | null): boolean {
  if (!value) {
    return true;
  }

  return !!value.match(/^[0-9a-z_-]+$/);
}

export function toFirstAnsweredUserIds(
  questionAnswers: QuestionAnswer[] | undefined
): string[] | undefined {
  if (!questionAnswers?.length) {
    return undefined;
  }

  const firstAnsweredUserIds: string[] = [];
  questionAnswers.map(
    (qa) =>
      qa.firstAnsweredUserId &&
      firstAnsweredUserIds.push(qa.firstAnsweredUserId)
  );

  if (!firstAnsweredUserIds?.length) {
    return undefined;
  }

  // remove duplicate value
  return Array.from(new Set(firstAnsweredUserIds));
}

export function toQaIDictionary(
  questionAnswers: QuestionAnswer[] | undefined
): QaIDictionary {
  if (!questionAnswers?.length) {
    return {};
  }

  return questionAnswers?.reduce(
    (arr: QaIDictionary, qa) => ((arr[qa.id] = qa), arr),
    {}
  );
}

export function formatSlug(slug: string): string {
  try {
    return (
      slug
        .trim()
        .toLowerCase()
        // eslint-disable-next-line no-irregular-whitespace
        .replace(/　+/g, " ")
        .replace(/\s+/g, "_")
        .replace(/-+/g, "_")
        .replace(/_+/g, "_")
        // replace full-width alphabet to half-width
        .replace(/[\uff01-\uff5e]/g, function (ch) {
          return String.fromCharCode(ch.charCodeAt(0) - 0xfee0);
        })
    );
  } catch (e) {
    errorWithoutThrowInProduction({
      errorInfo: { slug, stack: e?.stack },
      errorMessage: "Fail to format slug",
    });
    return slug;
  }
}
